import React, { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import "./GalleryActionConfirmModal.css";

function RecycleBinIcon({ phase }) {
  return (
    <div className={`gac-icon gac-icon--delete gac-stage--${phase}`} aria-hidden="true">
      <span className="gac-icon__halo" />
      <span className="gac-sheet gac-sheet--a" />
      <span className="gac-sheet gac-sheet--b" />
      <span className="gac-sheet gac-sheet--c" />
      <svg className="gac-bin" viewBox="0 0 120 120" fill="none">
        <g className="gac-bin__lid">
          <rect x="28" y="28" width="64" height="8" rx="3" />
          <rect x="50" y="22" width="20" height="8" rx="3" />
        </g>
        <path
          className="gac-bin__body"
          d="M36 40h48l-4.5 48c-.4 4.2-4 7.4-8.3 7.4H48.8c-4.3 0-7.9-3.2-8.3-7.4L36 40Z"
        />
        <g className="gac-bin__arrows">
          <path d="M52 62c0-4.4 3.6-8 8-8 2.6 0 4.9 1.2 6.4 3.1" />
          <path d="M66.4 54.4 68 59.6 63 58.2" />
          <path d="M68 70c0 4.4-3.6 8-8 8-2.6 0-4.9-1.2-6.4-3.1" />
          <path d="M53.6 77.6 52 72.4 57 73.8" />
        </g>
      </svg>
      {phase === "done" && <span className="gac-icon__check">✓</span>}
    </div>
  );
}

function DownloadPulseIcon({ phase }) {
  return (
    <div className={`gac-icon gac-icon--download gac-stage--${phase}`} aria-hidden="true">
      <span className="gac-icon__halo" />
      <span className="gac-dl__ring gac-dl__ring--a" />
      <span className="gac-dl__ring gac-dl__ring--b" />
      <span className="gac-packet gac-packet--a" />
      <span className="gac-packet gac-packet--b" />
      <span className="gac-packet gac-packet--c" />
      <svg className="gac-dl" viewBox="0 0 120 120" fill="none">
        <g className="gac-dl__arrow">
          <path d="M60 28v40" />
          <path d="M44 54 60 72 76 54" />
        </g>
        <path className="gac-dl__tray" d="M30 86h60c3.3 0 6 2.7 6 6v4H24v-4c0-3.3 2.7-6 6-6Z" />
        <path className="gac-dl__tray-lip" d="M24 96h72" />
      </svg>
      {phase === "done" && <span className="gac-icon__check">✓</span>}
    </div>
  );
}

function ProgressBar({ mode, progress, label, percentLabel }) {
  const clamped = Math.max(0, Math.min(100, progress));
  return (
    <div className={`gac-bar gac-bar--${mode}`}>
      <div className="gac-bar__labels">
        <span>{label}</span>
        <span>{percentLabel}</span>
      </div>
      <div className="gac-bar__rail" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(clamped)}>
        <div className="gac-bar__fill" style={{ width: `${clamped}%` }}>
          <span className="gac-bar__head" />
        </div>
        <span className="gac-bar__sheen" />
      </div>
    </div>
  );
}

/**
 * Confirm + progress modal for gallery delete / download actions.
 */
const GalleryActionConfirmModal = ({
  open = false,
  mode = "delete",
  itemCount = 0,
  itemNoun = "design",
  phase = "confirm",
  progress = 0,
  processed = 0,
  error = null,
  onConfirm = () => {},
  onClose = () => {},
}) => {
  const titleId = useId();
  const isDelete = mode === "delete";
  const isBusy = phase === "working";
  const canDismiss = phase !== "working";
  const noun = itemCount === 1 ? itemNoun : `${itemNoun}s`;
  const countLabel = `${itemCount} ${noun}`;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && canDismiss) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, canDismiss, onClose]);

  if (!open || typeof document === "undefined") return null;

  const title = isDelete
    ? phase === "done"
      ? "Designs deleted"
      : phase === "error"
        ? "Could not delete"
        : "Permanently delete?"
    : phase === "done"
      ? "Download complete"
      : phase === "error"
        ? "Could not download"
        : "Download to your device?";

  const message = isDelete
    ? phase === "done"
      ? `${countLabel} ${itemCount === 1 ? "has" : "have"} been permanently removed from My Designs.`
      : phase === "error"
        ? error || "Something went wrong while deleting. Please try again."
        : `The selected ${countLabel} will be permanently deleted from My Designs. This cannot be undone.`
    : phase === "done"
      ? `${countLabel} ${itemCount === 1 ? "has" : "have"} been saved to your device.`
      : phase === "error"
        ? error || "Something went wrong while downloading. Please try again."
        : `The selected ${countLabel} will be downloaded to your device. Keep this window open until the transfer finishes.`;

  const progressLabel = isDelete
    ? processed > 0
      ? `Removing ${processed} of ${itemCount}`
      : "Preparing to delete…"
    : processed > 0
      ? `Saving ${processed} of ${itemCount}`
      : "Preparing download…";

  const node = (
    <div className={`gac-root gac-root--${mode}`} role="presentation">
      <button
        type="button"
        className="gac-backdrop"
        aria-label="Close"
        disabled={!canDismiss}
        onClick={() => {
          if (canDismiss) onClose();
        }}
      />
      <div
        className={`gac-panel gac-panel--${mode} gac-panel--${phase}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {canDismiss && (
          <button type="button" className="gac-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        )}

        {isDelete ? <RecycleBinIcon phase={phase} /> : <DownloadPulseIcon phase={phase} />}

        <h2 id={titleId} className="gac-title">
          {title}
        </h2>
        <p className="gac-message">{message}</p>

        {(phase === "working" || phase === "done") && (
          <ProgressBar
            mode={mode}
            progress={phase === "done" ? 100 : progress}
            label={phase === "done" ? (isDelete ? "Removed" : "Saved") : progressLabel}
            percentLabel={`${Math.round(phase === "done" ? 100 : progress)}%`}
          />
        )}

        {phase === "confirm" && (
          <div className="gac-actions">
            <button type="button" className="gac-btn gac-btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className={`gac-btn ${isDelete ? "gac-btn--danger" : "gac-btn--primary"}`}
              onClick={onConfirm}
            >
              {isDelete ? "Delete permanently" : "Download now"}
            </button>
          </div>
        )}

        {phase === "error" && (
          <div className="gac-actions">
            <button type="button" className="gac-btn gac-btn--ghost" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className={`gac-btn ${isDelete ? "gac-btn--danger" : "gac-btn--primary"}`}
              onClick={onConfirm}
            >
              Try again
            </button>
          </div>
        )}

        {isBusy && <p className="gac-busy">Please wait — do not close this window.</p>}
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default GalleryActionConfirmModal;
